import { PageRequest } from '@/core/request/PageRequest';
import { useServiceError } from '@/hooks';
import notificationError from '@/utils/functions/notificationError';
import { message, notification } from 'antd';
import { useIntl } from 'react-intl';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import {
  accessoryActivateOrInactivate,
  accessoryListByVehicle,
  createVehiclesAccessory,
  deleteVehiclesAccessory,
  getAccessories,
  getAccessory,
  listVehiclesAccessories,
  updateVehiclesAccessory,
} from './Accessory.service';

import {
  CreateVehicleAccessoryRequest,
  ListAccessoriesRequest,
  ListVehicleAccessoryQuery,
  UpdateVehicleAccessoryRequest,
} from './Accessory.types';
import { queryClient } from '@/Root';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import replaceWords from '@/utils/functions/replaceWords';
import verifyIfHasVinculed from '@/utils/functions/verifyIfHasVinculed';

export function useVehicleAccessories(
  params?: ListVehicleAccessoryQuery,
  enabled = true,
) {
  const query = useInfiniteQuery(
    ['vehicles-acessories', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listVehiclesAccessories(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      enabled,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useAccessoryList(params?: ListAccessoriesRequest) {
  const query = useInfiniteQuery(
    ['acessories', params],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return getAccessories(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useAccessory() {
  const query = useMutation((id: number) => getAccessory(id));

  useServiceError(query.error);

  return query;
}

export function useCreateVehicleAccessory() {
  const intl = useIntl();

  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: CreateVehicleAccessoryRequest) => createVehiclesAccessory(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'customer.vehicle.accessory.notifications.create.success.title',
          }),
          description: intl.formatMessage({
            id: 'accessories.modal.create.message.success',
          }),
        });

        queryChanel({
          invalidateQueries: [['vehicles-acessories']],
          removeQueries: [['accessories/vehicle']],
        });
      },

      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) =>
              err.includes('There is already an accessory with this name')
                ? 'Já existe um acessório com este nome.'
                : err,
            ),
        });
      },
    },
  );

  return mutation;
}

export function useUpdateVehicleAccessory() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: UpdateVehicleAccessoryRequest) => updateVehiclesAccessory(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'accessories.modal.edit.message.success',
          }),
          description: intl.formatMessage({
            id: 'customer.vehicle.accessory.notifications.update.success.title',
          }),
        });

        queryClient.invalidateQueries(['vehicles-acessories']);
        queryClient.removeQueries(['accessories/vehicle']);
      },

      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) =>
              err.includes('There is already an accessory with this name')
                ? 'Já existe um acessório com este nome.'
                : err,
            ),
        });
      },
    },
  );

  return mutation;
}

export function useDeleteVehicleAccessory() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (accessoryId: number) => deleteVehiclesAccessory(accessoryId),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'customer.vehicle.accessory.remove.notification.success.message',
          }),
          description: intl.formatMessage({
            id: 'customer.vehicle.accessory.remove.notification.successDescription.description',
          }),
        });

        queryClient.invalidateQueries(['vehicles-acessories']);
        queryClient.removeQueries(['accessories/vehicle']);
      },

      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) =>
              verifyIfHasVinculed(err) ? replaceWords(err) : err,
            ),
        });
      },
    },
  );

  return mutation;
}

export function useActiveOrInactivateAccessory() {
  const intl = useIntl();

  const mutation = useMutation(
    (accessoryId: number) => accessoryActivateOrInactivate(accessoryId),
    {
      onSuccess() {
        message.success(
          intl.formatMessage({ id: 'accessories.status.message.success' }),
        );
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useAccessoryListByVehicle(vehicleId?: number) {
  const cache = queryClient.getQueryData([
    'accessories/vehicle',
    { vehicleId },
  ]);

  return useQuery(
    ['accessories/vehicle', { vehicleId }],
    () => accessoryListByVehicle(vehicleId),
    {
      enabled: !!vehicleId && !cache,
      onError(err) {
        notificationError(err);
      },
    },
  );
}
